import React, { useEffect, useRef, useState } from 'react';
import { Form, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import CustomButton from '@/components/CustomButton';
import CustomInput from '@/components/CustomInput';
import CustomModalWithoutButtons from '@/components/CustomModalWithoutButtons';
import { CustomDeleteModal } from '@/pages/Modals';
import CustomParagraph from '@/components/CustomParagraph';
import ShipmentsFilterPanel from '../../ShipmentFiltersPanel';
import VisibilitySelector from './VisibilitySelector';
import type {
  AppliedFilter,
  FilterArrayItem,
  FilterSetVisibility,
  SavedFilterSet,
} from '@/pages/Shipments/ControlTower/types';
import '@/pages/Shipments/Filters/Filters.css';
import '@/pages/Shipments/Filters/Filters.css';
import { Permissions } from '@/pages/Shipments/Filters/FilterDropdownMenus';

export interface SavedFilterSetModalProps {
  permissions?: Permissions;
  open: boolean;
  onClose: () => void;
  onDelete?: () => Promise<void> | void;
  editingFilterSet?: SavedFilterSet | null;
  currentAppliedFilters?: AppliedFilter[];
  currentFilterArray?: FilterArrayItem[];
  onSave: (data: {
    name: string;
    visibility: FilterSetVisibility;
    filters: FilterArrayItem[];
    appliedFilters: AppliedFilter[];
    teamIds: string[];
  }) => Promise<void>;
}

const SavedFilterSetModal: React.FC<SavedFilterSetModalProps> = ({
  open,
  onClose,
  permissions,
  editingFilterSet,
  currentAppliedFilters = [],
  currentFilterArray = [],
  onSave,
  onDelete,
}) => {
  const { t } = useTranslation('shipments');
  const [form] = Form.useForm();

  const isEditMode = Boolean(editingFilterSet);
  const canManageFilterSet = editingFilterSet?.isOwner ?? false;
  const [name, setName] = useState('');
  const [visibility, setVisibility] = useState<FilterSetVisibility>('personal');
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showFilterValidation, setShowFilterValidation] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const [modalAppliedFilters, setModalAppliedFilters] = useState<
    AppliedFilter[]
  >([]);
  const [modalFilterArray, setModalFilterArray] = useState<FilterArrayItem[]>(
    []
  );
  const [modalSearchQuery, setModalSearchQuery] = useState('');
  const [modalIsSearchVisible, setModalIsSearchVisible] = useState(false);

  const didRunInitialFetchRef = useRef(false);
  const popupContainerRef = useRef<HTMLDivElement | null>(null);

  const hasFilters =
    modalFilterArray.length > 0 || modalAppliedFilters.length > 0;

  useEffect(() => {
    if (!open) return;

    if (editingFilterSet) {
      const newName = editingFilterSet.name ?? '';
      const nextVisibility =
        (editingFilterSet.visibility as FilterSetVisibility) ?? 'personal';

      setName(newName);
      form.setFieldsValue({ name: newName });
      setVisibility(nextVisibility);

      const mappedTeamIds = editingFilterSet.teamIds?.length
        ? editingFilterSet.teamIds.map((id) => String(id))
        : (editingFilterSet.teamsDisplay?.map((team: { id: string }) =>
            String(team.id)
          ) ?? []);

      setSelectedTeamIds(mappedTeamIds);
      setModalAppliedFilters(editingFilterSet.appliedFilters ?? []);
      setModalFilterArray(editingFilterSet.filters ?? []);
    } else {
      setName('');
      form.resetFields();
      setVisibility('personal');
      setSelectedTeamIds([]);
      setModalAppliedFilters(currentAppliedFilters ?? []);
      setModalFilterArray(currentFilterArray ?? []);
    }

    setModalSearchQuery('');
    setModalIsSearchVisible(false);
    setShowFilterValidation(false);
    setIsDeleteModalOpen(false);
    didRunInitialFetchRef.current = false;
  }, [open, editingFilterSet, currentAppliedFilters, currentFilterArray, form]);
  useEffect(() => {
    if (hasFilters && showFilterValidation) {
      setShowFilterValidation(false);
      form.setFields([{ name: 'filters', errors: [] }]);
    }
  }, [hasFilters, showFilterValidation, form]);

  const handleOk = async () => {
    setShowFilterValidation(true);

    try {
      await form.validateFields();
    } catch {
      return;
    }

    if (!name.trim()) {
      form.setFields([
        {
          name: 'name',
          errors: [t('tabs.modal.validation.nameRequired')],
        },
      ]);
      return;
    }

    if (!hasFilters) {
      form.setFields([
        {
          name: 'filters',
          errors: [t('tabs.modal.validation.filterRequired')],
        },
      ]);
      return;
    }

    if (visibility === 'team' && selectedTeamIds.length === 0) {
      form.setFields([
        {
          name: 'visibilityTeams',
          errors: [t('tabs.modal.validation.teamRequired')],
        },
      ]);
      return;
    }

    setSaving(true);
    try {
      await onSave({
        name: name.trim(),
        visibility,
        filters: modalFilterArray,
        appliedFilters: modalAppliedFilters,
        teamIds: visibility === 'team' ? selectedTeamIds : [],
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!onDelete || !canManageFilterSet) return;

    setDeleting(true);
    try {
      await onDelete();
      setIsDeleteModalOpen(false);
      onClose();
    } finally {
      setDeleting(false);
    }
  };

  const title = isEditMode
    ? t('tabs.modal.editTitle', 'Edit filter set')
    : t('tabs.modal.newTitle', 'New filter set');

  const okText = isEditMode
    ? t('tabs.modal.updateCTA', 'Update filter set')
    : t('tabs.modal.saveCTA', 'Save filter set');

  const deleteTitle =
    visibility === 'personal'
      ? t('tabs.modal.deletePersonalTitle', 'Delete filter set')
      : t('tabs.modal.deleteSharedTitle', 'Delete shared filter set');

  const deleteMessage =
    visibility === 'personal'
      ? t(
          'tabs.modal.deletePersonalMessage',
          'Deleting this filter set will remove it from your tabs and saved filters. This action can’t be undone.'
        )
      : t(
          'tabs.modal.deleteSharedMessage',
          'This filter set is shared with other users. Deleting it will remove it from their tabs and saved filters. This action can’t be undone.'
        );

  return (
    <>
      <CustomModalWithoutButtons
        modalOpen={open}
        setModalOpen={(nextOpen) => {
          if (!nextOpen) onClose();
        }}
        modalTitle={title}
        modalSize={690}
        modalBorderRadius='16px'
        fontSize='24px'
        lineHeight='32px'
        closeIconSize='20px'
        className='saved-filter-set-modal--exact'
      >
        <div
          ref={popupContainerRef}
          className='saved-filter-set-modal saved-filter-set-modal--exact'
        >
          <Form form={form} layout='vertical' className='saved-filter-set-form'>
            <div className='saved-filter-set-modal__body'>
              <div className='saved-filter-set-modal__section saved-filter-set-modal__section--name'>
                <CustomParagraph
                  text={t('tabs.modal.nameLabel', 'Name')}
                  className='saved-filter-set-modal__section-title'
                  marginBottom='8px'
                />

                <Form.Item
                  name='name'
                  rules={[
                    {
                      required: true,
                      message: t('tabs.modal.validation.nameRequired'),
                    },
                  ]}
                  className='saved-filter-set-modal__field'
                >
                  <CustomInput
                    value={name}
                    onChange={(e) => {
                      const value = e.target.value;
                      setName(value);
                      form.setFieldsValue({ name: value });
                    }}
                    placeholder={t(
                      'tabs.modal.namePlaceholder',
                      'Save filter set name'
                    )}
                    height='36px'
                    width='100%'
                    border='0px'
                    bgColor='var(--light-gray)'
                    textColor='var(--text-primary)'
                    borderRadius='10px'
                    placeholderColor='var(--text-secondary)'
                    lineHeight='42px'
                    className='saved-filter-set-modal__input'
                  />
                </Form.Item>
              </div>

              <div className='saved-filter-set-modal__section saved-filter-set-modal__section--filters'>
                <CustomParagraph
                  text={t('tabs.modal.filtersLabel', 'Filters')}
                  className='saved-filter-set-modal__section-title'
                  marginBottom='4px'
                />
                <CustomParagraph
                  text={t(
                    'tabs.modal.filtersHelp',
                    'Add at least one filter to save this filter set.'
                  )}
                  fontSize={12}
                  className='saved-filter-set-modal__section-subtitle'
                  marginBottom='12px'
                />

                <Form.Item
                  name='filters'
                  validateStatus={
                    showFilterValidation && !hasFilters ? 'error' : ''
                  }
                  help={
                    showFilterValidation && !hasFilters
                      ? t('tabs.modal.validation.filterRequired')
                      : undefined
                  }
                  className='saved-filter-set-modal__field'
                >
                  <ShipmentsFilterPanel
                    dropdownMaxHeight='300px'
                    savedFilterKey='shipment'
                    auditSearch
                    token=''
                    permissions={permissions}
                    appliedFilters={modalAppliedFilters}
                    setAppliedFilters={setModalAppliedFilters}
                    filterArray={modalFilterArray}
                    setFilterArray={setModalFilterArray}
                    setFiltersPayload={() => {}}
                    searchQuery={modalSearchQuery}
                    setSearchQuery={setModalSearchQuery}
                    isSearchVisible={modalIsSearchVisible}
                    setIsSearchVisible={setModalIsSearchVisible}
                    page={1}
                    pageSize={50}
                    onRunSearch={() => {}}
                    didRunInitialFetchRef={didRunInitialFetchRef}
                    showSearch={false}
                    showSaveFilterSetLink={false}
                    persistState={false}
                    popupContainer={document.body}
                  />
                </Form.Item>
              </div>

              <div className='saved-filter-set-modal__section saved-filter-set-modal__section--visibility'>
                <CustomParagraph
                  text={t('tabs.modal.visibilityLabel', 'Visibility')}
                  className='saved-filter-set-modal__section-title'
                />
                <CustomParagraph
                  text={t(
                    'tabs.modal.visibilityHelp',
                    'Set who can see this filter set.'
                  )}
                  className='saved-filter-set-modal__section-subtitle'
                  marginBottom='12px'
                />

                <VisibilitySelector
                  value={visibility}
                  onChange={setVisibility}
                  selectedTeamIds={selectedTeamIds}
                  onTeamIdsChange={setSelectedTeamIds}
                />
              </div>
            </div>

            <div
              className={`saved-filter-set-modal__footer ${
                isEditMode
                  ? 'saved-filter-set-modal__footer--with-delete'
                  : 'saved-filter-set-modal__footer--no-delete'
              }`}
            >
              {isEditMode && canManageFilterSet && (
                <CustomButton
                  text={t('tabs.modal.deleteCTA', 'Delete')}
                  onClick={() => setIsDeleteModalOpen(true)}
                  bgColor='transparent'
                  color='var(--danger)'
                  fontSize='16px'
                  fontWeight='500'
                  boxShadow='none'
                  height='48px'
                  padding='0'
                  className='saved-filter-set-modal__delete-btn'
                  isFormValid={saving || deleting}
                />
              )}

              <div className='saved-filter-set-modal__footer-actions'>
                <CustomButton
                  onClick={onClose}
                  text={t('tabs.modal.cancelCTA', 'Cancel')}
                  bgColor='transparent'
                  color='var(--text-primary)'
                  fontSize='16px'
                  fontWeight='500'
                  boxShadow='none'
                  height='48px'
                  padding='0 12px'
                  isFormValid={saving || deleting}
                />

                <CustomButton
                  text={okText}
                  width='161px'
                  height='48px'
                  borderRadius='8px'
                  bgColor='#000'
                  color='#fff'
                  border='0px'
                  fontSize='16px'
                  fontWeight='500'
                  onClick={handleOk}
                  isFormValid={saving || deleting}
                  padding='0 20px'
                  loader={saving ? <Spin size='small' /> : undefined}
                />
              </div>
            </div>
          </Form>
        </div>
      </CustomModalWithoutButtons>

      <CustomDeleteModal
        visible={isDeleteModalOpen}
        onClose={() => {
          if (!deleting) {
            setIsDeleteModalOpen(false);
          }
        }}
        onConfirm={handleConfirmDelete}
        title={deleteTitle}
        message={deleteMessage}
        cancelText={t('tabs.modal.cancelCTA', 'Cancel')}
        confirmText={t('tabs.modal.deleteCTA', 'Delete')}
        confirmColor='var(--text-primary)'
        width={542}
        height={208}
        titleFontSize={24}
        messageFontSize={16}
        buttonHeight={48}
        closeIconSize={16}
        disableConfirmBtn={deleting}
        addButton={deleting ? <Spin size='small' /> : undefined}
      />
    </>
  );
};

export default SavedFilterSetModal;
